/*
  Scripts de la surcouche « néon » (sans dépendance, chargé en defer).
  - Apparition au défilement ([data-neo-reveal])
  - Vidéo à la demande (<neo-video>)
  - Offre Duo sur la fiche produit (<neo-offer-picker>) : intercepte l'envoi du
    formulaire Dawn quand « Duo » est choisi et ajoute les 2 miniatures d'un coup.
  - Achat rapide (<neo-quick-buy>), relance Duo dans le panier ([data-neo-add])
  - Barre d'achat fixe mobile (<neo-sticky-atc>)
  La remise elle-même est appliquée par Shopify (réduction automatique), jamais ici.
*/
(() => {
  const root = document.documentElement;
  window.NEO_READY = true;

  /* ---------- Panier ---------- */
  const NEO = (window.NEO = window.NEO || {});

  NEO.addItems = async (items, opener) => {
    const drawer = document.querySelector('cart-drawer');
    const body = { items };
    if (drawer) {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
      if (opener) drawer.setActiveElement(opener);
    }
    const res = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Ajout impossible');
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'neo', cartData: data });
    }
    if (window.location.pathname === window.routes.cart_url) {
      window.location.reload();
    } else if (drawer) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(data);
    } else {
      window.location = window.routes.cart_url;
    }
    return data;
  };

  NEO.duoItems = (firstId, secondId) =>
    String(firstId) === String(secondId)
      ? [{ id: Number(firstId), quantity: 2 }]
      : [
          { id: Number(firstId), quantity: 1 },
          { id: Number(secondId), quantity: 1 },
        ];

  const busy = (btn, on) => {
    if (!btn) return;
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    btn.classList.toggle('loading', on);
  };

  /* ---------- Apparition au défilement ---------- */
  const revealAll = () => {
    const els = document.querySelectorAll('[data-neo-reveal]:not(.is-in)');
    if (!root.classList.contains('neo-motion') || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
  };
  revealAll();
  document.addEventListener('shopify:section:load', revealAll);

  /* ---------- Vidéo ---------- */
  if (!customElements.get('neo-video')) {
    customElements.define(
      'neo-video',
      class extends HTMLElement {
        connectedCallback() {
          this.btn = this.querySelector('.neo-video__play');
          this.btn?.addEventListener('click', () => this.play(true));
          if (this.dataset.autoplay === 'true' && root.classList.contains('neo-motion') && 'IntersectionObserver' in window) {
            const io = new IntersectionObserver((entries) => {
              entries.forEach((e) => {
                const v = this.activeVideo();
                if (!v) return;
                if (e.isIntersecting) this.play(false);
                else v.pause();
              });
            }, { threshold: 0.45 });
            io.observe(this);
          }
        }
        activeVideo() {
          const vids = [...this.querySelectorAll('video')];
          return vids.find((v) => v.offsetParent !== null) || vids[0];
        }
        play(withControls) {
          const v = this.activeVideo();
          if (!v) return;
          v.querySelectorAll('source[data-src]').forEach((s) => {
            s.src = s.dataset.src;
            s.removeAttribute('data-src');
          });
          if (v.readyState === 0) v.load();
          v.muted = !withControls ? true : v.muted;
          if (withControls) v.controls = true;
          v.play().catch(() => {});
          this.classList.add('is-playing');
        }
      }
    );
  }

  /* ---------- Offre Duo (fiche produit) ---------- */
  if (!customElements.get('neo-offer-picker')) {
    customElements.define(
      'neo-offer-picker',
      class extends HTMLElement {
        connectedCallback() {
          this.second = this.querySelector('[data-neo-second]');
          this.secondSelect = this.querySelector('select');
          this.radios = [...this.querySelectorAll('input[name^="neo-offer"]')];
          this.radios.forEach((r) => r.addEventListener('change', () => this.sync()));
          const wanted = new URLSearchParams(window.location.search).get('offre');
          if (wanted === 'duo') {
            const duo = this.radios.find((r) => r.value === 'duo');
            if (duo) duo.checked = true;
          }
          this.sync();
          this.bindForm();
          if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
            subscribe(PUB_SUB_EVENTS.variantChange, (e) => this.onVariant(e.data?.variant));
          }
        }
        get isDuo() {
          return this.radios.some((r) => r.checked && r.value === 'duo');
        }
        sync() {
          if (this.second) this.second.hidden = !this.isDuo;
          const btnText = document.querySelector(`#ProductSubmitButton-${this.dataset.sectionId} > span`);
          if (btnText && !btnText.dataset.neoOriginal) btnText.dataset.neoOriginal = btnText.textContent.trim();
          if (btnText && !btnText.closest('button').disabled) {
            btnText.textContent = this.isDuo ? this.dataset.duoLabel : btnText.dataset.neoOriginal;
          }
          // « Acheter maintenant » (paiement accéléré) n'achète qu'une variante : masqué en mode Duo.
          document.getElementById(`MainProduct-${this.dataset.sectionId}`)?.classList.toggle('neo-duo-on', this.isDuo);
          document.dispatchEvent(new CustomEvent('neo:offer', { detail: { duo: this.isDuo } }));
        }
        onVariant(variant) {
          if (!variant || !this.secondSelect) return;
          if (!this.secondSelect.dataset.touched) this.secondSelect.value = String(variant.id);
          const btnText = document.querySelector(`#ProductSubmitButton-${this.dataset.sectionId} > span`);
          if (btnText) btnText.dataset.neoOriginal = btnText.textContent.trim();
          this.sync();
        }
        bindForm() {
          this.secondSelect?.addEventListener('change', () => (this.secondSelect.dataset.touched = '1'));
          const pf = document.querySelector(`#MainProduct-${this.dataset.sectionId} product-form, product-form[data-section-id="${this.dataset.sectionId}"]`);
          if (!pf) return;
          pf.addEventListener(
            'submit',
            async (evt) => {
              if (!this.isDuo) return;
              evt.preventDefault();
              evt.stopPropagation();
              const form = evt.target;
              const idInput = form.querySelector('[name="id"]');
              const btn = form.querySelector('[type="submit"]');
              if (!idInput || idInput.disabled) return;
              const errorBox = this.querySelector('.neo-buy-error');
              if (errorBox) errorBox.hidden = true;
              busy(btn, true);
              try {
                await NEO.addItems(NEO.duoItems(idInput.value, this.secondSelect.value), btn);
              } catch (e) {
                if (errorBox) {
                  errorBox.textContent = e.message;
                  errorBox.hidden = false;
                }
              } finally {
                busy(btn, false);
              }
            },
            true
          );
        }
      }
    );
  }

  /* ---------- Achat rapide (section finale de l'accueil) ---------- */
  if (!customElements.get('neo-quick-buy')) {
    customElements.define(
      'neo-quick-buy',
      class extends HTMLElement {
        connectedCallback() {
          this.img = this.querySelector('.neo-buybox__media img');
          this.second = this.querySelector('[data-neo-second]');
          this.secondSelect = this.querySelector('[data-neo-second] select');
          this.btn = this.querySelector('[data-neo-submit]');
          this.err = this.querySelector('.neo-buy-error');
          this.addEventListener('change', (e) => {
            if (e.target.name === 'neo-qb-variant') this.onVariant(e.target);
            this.sync();
          });
          this.btn?.addEventListener('click', () => this.submit());
          this.sync();
        }
        get variantInput() {
          return this.querySelector('input[name="neo-qb-variant"]:checked');
        }
        get isDuo() {
          return !!this.querySelector('input[name="neo-qb-offer"][value="duo"]:checked');
        }
        onVariant(input) {
          const stage = this.querySelector('[data-neo-stage]');
          if (stage) {
            // Scène fixe : la nouvelle voiture apparaît en fondu par-dessus l'ancienne, qui reste opaque
            // dessous jusqu'à la fin du fondu (pas de creux de transparence, aucun saut de position).
            const next = stage.querySelector(`[data-neo-color="${input.dataset.color}"]`);
            const prev = stage.querySelector('.neo-stage__car.is-active');
            if (next && next !== prev) {
              stage.querySelectorAll('.neo-stage__car.was-active').forEach((el) => el.classList.remove('was-active'));
              prev?.classList.replace('is-active', 'was-active');
              next.classList.add('is-active');
              clearTimeout(this.fadeTimer);
              this.fadeTimer = setTimeout(() => prev?.classList.remove('was-active'), 520);
            }
          } else if (this.img && input.dataset.img) {
            this.img.src = input.dataset.img;
            this.img.removeAttribute('srcset');
            this.img.alt = input.dataset.alt || this.img.alt;
          }
          if (this.secondSelect && !this.secondSelect.dataset.touched) this.secondSelect.value = input.value;
        }
        sync() {
          if (this.second) this.second.hidden = !this.isDuo;
          if (this.btn) this.btn.querySelector('span').textContent = this.isDuo ? this.dataset.duoLabel : this.dataset.singleLabel;
          if (this.secondSelect) this.secondSelect.onchange = () => (this.secondSelect.dataset.touched = '1');
        }
        async submit() {
          const v = this.variantInput;
          if (!v) return;
          if (this.err) this.err.hidden = true;
          busy(this.btn, true);
          try {
            const items = this.isDuo ? NEO.duoItems(v.value, this.secondSelect.value) : [{ id: Number(v.value), quantity: 1 }];
            await NEO.addItems(items, this.btn);
          } catch (e) {
            if (this.err) {
              this.err.textContent = e.message;
              this.err.hidden = false;
            }
          } finally {
            busy(this.btn, false);
          }
        }
      }
    );
  }

  /* ---------- Relance « 2e miniature » dans le panier ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-neo-add]');
    if (!btn) return;
    e.preventDefault();
    busy(btn, true);
    try {
      await NEO.addItems([{ id: Number(btn.dataset.neoAdd), quantity: 1 }], btn);
    } catch (err) {
      btn.textContent = err.message;
    } finally {
      busy(btn, false);
    }
  });

  /* ---------- Barre d'achat fixe (mobile) ---------- */
  if (!customElements.get('neo-sticky-atc')) {
    customElements.define(
      'neo-sticky-atc',
      class extends HTMLElement {
        connectedCallback() {
          const target = document.getElementById(this.dataset.target);
          const btn = this.querySelector('button');
          if (!target || !('IntersectionObserver' in window)) return;
          let footerVisible = false;
          let targetVisible = true;
          const update = () => this.classList.toggle('is-visible', !targetVisible && !footerVisible && window.scrollY > 200);
          new IntersectionObserver(([e]) => {
            targetVisible = e.isIntersecting;
            update();
          }).observe(target);
          const footer = document.querySelector('.footer');
          if (footer)
            new IntersectionObserver(([e]) => {
              footerVisible = e.isIntersecting;
              update();
            }).observe(footer);
          btn?.addEventListener('click', () => {
            const submit = target.querySelector('[type="submit"]');
            if (submit && !submit.disabled) submit.click();
            else target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
          document.addEventListener('neo:offer', (ev) => {
            const label = this.querySelector('[data-neo-sticky-label]');
            if (label) label.textContent = ev.detail.duo ? this.dataset.duoLabel : this.dataset.singleLabel;
            const price = this.querySelector('[data-neo-sticky-price]');
            if (price) price.textContent = ev.detail.duo ? this.dataset.duoPrice : this.dataset.singlePrice;
          });
        }
      }
    );
  }
})();

/* Bandeau cookies Shopify : texte court (le bandeau est ajouté par Shopify après le chargement de la page).
   Les boutons et le lien vers la politique de confidentialité sont conservés tels quels. */
(() => {
  if (!/^fr/i.test(document.documentElement.lang || '')) return;
  const shorten = () => {
    const banner = document.getElementById('shopify-pc__banner');
    if (!banner) return false;
    const title = banner.querySelector('#shopify-pc__banner__body-title');
    const text = banner.querySelector('.shopify-pc__banner__body p');
    const link = text && text.querySelector('a');
    if (title && link && !banner.dataset.neoShort) {
      banner.dataset.neoShort = '1';
      title.textContent = 'Cookies';
      link.textContent = 'En savoir plus';
      text.replaceChildren('Avec votre accord, nous et nos partenaires, dont Shopify, utilisons des cookies pour personnaliser votre visite, mesurer l’audience et afficher des publicités. ', link);
    }
    return true;
  };
  if (shorten()) return;
  const observer = new MutationObserver(() => shorten() && observer.disconnect());
  observer.observe(document.body, { childList: true });
  setTimeout(() => observer.disconnect(), 30000);
})();
